import type { Locale } from "../../../shared/i18n";

/**
 * Display formatting matches the render dictionary: a readable date on screen
 * ("28 авг 2026"), a compact numeric one on paper ("28.08.2026").
 *
 * An unparsable value is returned verbatim rather than guessed at — user
 * content is untrusted and never reinterpreted.
 */
export function formatDate(
  value: unknown,
  locale: Locale,
  mode: "display" | "print",
): string {
  if (typeof value !== "string" || value === "") return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(
    locale,
    mode === "print"
      ? { day: "2-digit", month: "2-digit", year: "numeric" }
      : { day: "numeric", month: "short", year: "numeric" },
  );
}

export function formatTime(value: unknown): string {
  if (typeof value !== "string" || value === "") return "";
  // `HH:mm` and `HH:mm:ss` both come off the wire; the seconds are noise here.
  const match = /^\d{2}:\d{2}/.exec(value);
  if (match) return match[0];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toTimeString().slice(0, 5);
}

export function formatDateTime(
  value: unknown,
  locale: Locale,
  mode: "display" | "print",
): string {
  if (typeof value !== "string" || value === "") return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${formatDate(value, locale, mode)}, ${parsed
    .toTimeString()
    .slice(0, 5)}`;
}

/** `datetime-local` inputs reject the trailing zone and seconds. */
export function toDateTimeLocalValue(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.slice(0, 16);
}

export function asText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return "";
}

export function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}
